package com.evoapps.evoshift.viewmodels

import com.evoapps.evoshift.domain.util.AppClock
import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.evoapps.evoshift.Route
import com.evoapps.evoshift.domain.model.Capability
import com.evoapps.evoshift.domain.model.ChecklistProblem
import com.evoapps.evoshift.domain.model.ChecklistTemplate
import com.evoapps.evoshift.domain.model.Checklists
import com.evoapps.evoshift.domain.model.InvalidChecklistException
import com.evoapps.evoshift.domain.model.ShiftTemplate
import com.evoapps.evoshift.domain.repository.UserRepository
import com.evoapps.evoshift.domain.repository.WorkplaceRepository
import com.evoapps.evoshift.domain.usecase.auth.GetCurrentUserUseCase
import com.evoapps.evoshift.domain.usecase.billing.ObservePlanActiveUseCase
import com.evoapps.evoshift.domain.usecase.checklist.DeleteChecklistTemplateUseCase
import com.evoapps.evoshift.domain.usecase.checklist.ObserveChecklistTemplatesUseCase
import com.evoapps.evoshift.domain.usecase.checklist.ObserveDayChecklistsUseCase
import com.evoapps.evoshift.domain.usecase.checklist.SaveChecklistTemplateUseCase
import com.evoapps.evoshift.domain.usecase.checklist.ShiftChecklists
import com.evoapps.evoshift.domain.usecase.checklist.newChecklistTemplate
import com.evoapps.evoshift.session.WorkplaceSessionManager
import com.evoapps.evoshift.ui.util.UiText
import com.evoapps.evoshift.ui.util.toUiText
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.checklist_deleted
import evoshift.composeapp.generated.resources.checklist_saved
import evoshift.composeapp.generated.resources.error_save_failed
import kotlinx.coroutines.ExperimentalCoroutinesApi
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.catch
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.distinctUntilChanged
import kotlinx.coroutines.flow.emptyFlow
import kotlinx.coroutines.flow.first
import kotlinx.coroutines.flow.flatMapLatest
import kotlinx.coroutines.flow.flowOf
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlinx.datetime.DatePeriod
import kotlinx.datetime.LocalDate
import kotlinx.datetime.TimeZone
import kotlinx.datetime.minus
import kotlinx.datetime.plus
import kotlinx.datetime.todayIn

/** The two parts of the Checklists screen. */
enum class ChecklistsTab { DAY, TEMPLATES }

data class ChecklistsUiState(
    /** Only until the first data arrives. */
    val isLoading: Boolean = true,
    val workplaceId: String? = null,
    val tab: ChecklistsTab = ChecklistsTab.DAY,
    val date: LocalDate,
    val today: LocalDate,
    val capabilities: Set<Capability> = emptySet(),
    /** The day's assigned shifts with their checklists, by planned start. */
    val day: List<ShiftChecklists> = emptyList(),
    /** Who is who on the day's shifts. */
    val names: Map<String, String> = emptyMap(),
    val templates: List<ChecklistTemplate> = emptyList(),
    /** What a template can be attached to. */
    val shiftTemplates: List<ShiftTemplate> = emptyList(),
    /** The template in the editor (a new one has a blank id); null when closed. */
    val editing: ChecklistTemplate? = null,
    val editorProblems: Set<ChecklistProblem> = emptySet(),
    val isSaving: Boolean = false,
    val message: UiText? = null,
    val error: UiText? = null
) {
    val canEdit: Boolean get() = capabilities.shows(Gate.CHECKLIST_EDITING)
}

/**
 * SH-121, the managers' Checklists screen: the day's checklists per shift (today, or any day
 * back for the history) and the workplace's templates with their editor. Saves are optimistic.
 */
@OptIn(ExperimentalCoroutinesApi::class)
class ChecklistsViewModel(
    private val args: Route.Checklists,
    private val getCurrentUserUseCase: GetCurrentUserUseCase,
    private val sessionManager: WorkplaceSessionManager,
    private val observeTemplates: ObserveChecklistTemplatesUseCase,
    private val observeDay: ObserveDayChecklistsUseCase,
    private val saveTemplate: SaveChecklistTemplateUseCase,
    private val deleteTemplate: DeleteChecklistTemplateUseCase,
    private val workplaceRepository: WorkplaceRepository,
    private val userRepository: UserRepository,
    private val plans: ObservePlanActiveUseCase? = null,
    private val today: () -> LocalDate = { AppClock.todayIn(TimeZone.currentSystemDefault()) }
) : ViewModel() {

    private val _uiState = MutableStateFlow(today().let { now ->
        ChecklistsUiState(date = args.date?.let { runCatching { LocalDate.parse(it) }.getOrNull() } ?: now, today = now)
    })
    val uiState: StateFlow<ChecklistsUiState> = _uiState.asStateFlow()

    private val workplaceFlow =
        (args.workplaceId?.let { flowOf(it) } ?: sessionManager.currentWorkplaceId).distinctUntilChanged()

    init {
        observe()
    }

    private fun observe() {
        viewModelScope.launch {
            workplaceFlow.flatMapLatest { wp ->
                if (wp == null) flowOf(null)
                else capabilitiesAt(getCurrentUserUseCase(), plans, wp).map { wp to it }
            }.catch { e -> _uiState.update { it.copy(isLoading = false, error = e.toUiText()) } } // SH-188
                .collect { loaded ->
                if (loaded == null) {
                    _uiState.update { it.copy(isLoading = false, workplaceId = null) }
                    return@collect
                }
                val (wp, caps) = loaded
                // Not a manager here: nothing to load (the screen is not offered either).
                _uiState.update { it.copy(workplaceId = wp, capabilities = caps, isLoading = it.isLoading && caps.shows(Gate.CHECKLISTS)) }
                if (caps.shows(Gate.CHECKLISTS)) {
                    val shiftTemplates = workplaceRepository.getShiftTemplates(wp).getOrDefault(emptyList())
                    val names = userRepository.getAssignableEmployees(wp).getOrNull().orEmpty().associate { it.uid to it.displayName }
                    _uiState.update { it.copy(shiftTemplates = shiftTemplates, names = it.names + names) }
                }
            }
        }
        viewModelScope.launch {
            combine(workplaceFlow, _uiState.map { it.date }.distinctUntilChanged(), _uiState.map { it.capabilities.shows(Gate.CHECKLISTS) }.distinctUntilChanged()) { wp, date, allowed ->
                Triple(wp, date, allowed)
            }.flatMapLatest { (wp, date, allowed) ->
                if (wp == null || !allowed) emptyFlow()
                else combine(observeTemplates(wp), observeDay(wp, date.toString())) { templates, day -> templates to day }
            }.catch { e -> _uiState.update { it.copy(isLoading = false, error = e.toUiText()) } } // SH-188
                .collect { (templates, day) ->
                _uiState.update { state ->
                    // Names on the shifts (older documents) fill in people who have left since.
                    val fromShifts = day.mapNotNull { row -> row.shift.userId?.let { id -> row.shift.userDisplayName?.let { id to it } } }.toMap()
                    state.copy(isLoading = false, templates = templates, day = day, names = fromShifts + state.names)
                }
            }
        }
    }

    fun selectTab(tab: ChecklistsTab) = _uiState.update { it.copy(tab = tab) }

    fun previousDay() = _uiState.update { it.copy(date = it.date.minus(DatePeriod(days = 1))) }

    /** Not past today: the future has nothing ticked yet. */
    fun nextDay() = _uiState.update { if (it.date < it.today) it.copy(date = it.date.plus(DatePeriod(days = 1))) else it }

    fun goToToday() = _uiState.update { it.copy(date = it.today) }

    fun startNew() {
        val wp = _uiState.value.workplaceId ?: return
        _uiState.update { it.copy(editing = newChecklistTemplate(wp), editorProblems = emptySet()) }
    }

    fun edit(template: ChecklistTemplate) = _uiState.update { it.copy(editing = template, editorProblems = emptySet()) }

    /** The editor's form changed. Problems shown after a refused save clear as they are fixed. */
    fun updateEditing(template: ChecklistTemplate) = _uiState.update { state ->
        val problems = if (state.editorProblems.isEmpty()) emptySet()
        else Checklists.problems(SaveChecklistTemplateUseCase.normalised(template)).intersect(state.editorProblems)
        state.copy(editing = template, editorProblems = problems)
    }

    fun cancelEditing() = _uiState.update { it.copy(editing = null, editorProblems = emptySet()) }

    fun saveEditing() {
        val state = _uiState.value
        val template = state.editing ?: return
        if (state.isSaving || !state.canEdit) return
        val problems = Checklists.problems(SaveChecklistTemplateUseCase.normalised(template))
        if (problems.isNotEmpty()) {
            _uiState.update { it.copy(editorProblems = problems) }
            return
        }
        _uiState.update { it.copy(isSaving = true) }
        viewModelScope.launch {
            val editor = getCurrentUserUseCase().first()?.uid.orEmpty()
            saveTemplate(template, editor)
                .onSuccess { _uiState.update { it.copy(isSaving = false, editing = null, editorProblems = emptySet(), message = UiText.Resource(Res.string.checklist_saved)) } }
                .onFailure { e ->
                    _uiState.update {
                        if (e is InvalidChecklistException) it.copy(isSaving = false, editorProblems = e.problems)
                        else it.copy(isSaving = false, error = UiText.Resource(Res.string.error_save_failed, e.toUiText()))
                    }
                }
        }
    }

    /** Removes [template] at once; a failed delete puts it back. Checklists ticked with it stay. */
    fun delete(template: ChecklistTemplate) {
        val state = _uiState.value
        val wp = state.workplaceId ?: return
        if (!state.canEdit) return
        val before = state.templates
        _uiState.update { it.copy(templates = it.templates.filterNot { t -> t.id == template.id }, editing = null) }
        viewModelScope.launch {
            deleteTemplate(wp, template.id)
                .onSuccess { _uiState.update { it.copy(message = UiText.Resource(Res.string.checklist_deleted)) } }
                .onFailure { e -> _uiState.update { it.copy(templates = before, error = UiText.Resource(Res.string.error_save_failed, e.toUiText())) } }
        }
    }

    fun messageShown() = _uiState.update { it.copy(message = null, error = null) }
}
